import React from 'react';
import { Building2, Check, Loader2 } from 'lucide-react';
import { useMyAccounts } from '../../hooks/useMyAccounts';
import { MenuItem } from '../ui';

interface AccountSwitcherProps {
  /** Called once a switch has been kicked off, so the host menu can close. */
  onSwitchStarted?: () => void;
}

/**
 * Lists the accounts the signed-in person owns and switches between them.
 *
 * Renders nothing unless the person actually has more than one account, so a single-account
 * customer — the overwhelming majority — never sees it. Deliberately styled as a normal menu
 * section rather than a warning banner: this is the person's own account, not impersonation.
 *
 * On desktop `AccountSwitcherNavButton` puts the same switch one click away in the navbar; this
 * stays as the version that works everywhere, including phones.
 */
const AccountSwitcher: React.FC<AccountSwitcherProps> = ({ onSwitchStarted }) => {
  const { accounts, hasMultiple, switchingTo, switchTo } = useMyAccounts();

  if (!hasMultiple) return null;

  return (
    <div className="p-1.5 border-t border-ink-100">
      <div className="px-2.5 py-1.5 text-11 font-semibold uppercase tracking-wide text-ink-400">
        Your accounts
      </div>

      {accounts.map(account => {
        const isSwitching = switchingTo === account.tenantId;

        return (
          <MenuItem
            key={account.tenantId}
            onClick={() => {
              if (account.isCurrent) return;
              onSwitchStarted?.();
              void switchTo(account);
            }}
            // Only disabled while a switch is in flight. The current account stays undimmed —
            // MenuItem fades disabled rows, and fading the account you are actually in reads wrong.
            disabled={switchingTo !== null}
            aria-current={account.isCurrent ? 'true' : undefined}
            className="rounded-md"
          >
            {isSwitching ? (
              <Loader2 className="h-4 w-4 text-ink-400 animate-spin shrink-0" />
            ) : (
              <Building2 className="h-4 w-4 text-ink-400 shrink-0" />
            )}
            <span className="flex-1 min-w-0 truncate">{account.tenantName}</span>
            {account.isCurrent && <Check className="h-4 w-4 text-accent-600 shrink-0" />}
          </MenuItem>
        );
      })}
    </div>
  );
};

export default AccountSwitcher;
